/**
 * Time-entry mutation — notes edit (with Harvest write-back).
 *
 * Optimistic flow:
 *   1. Patch the cached ReportDetail in place — the entry's notes
 *      flip immediately on Cmd+Enter.
 *   2. Server roundtrip writes through to Harvest first; we reconcile
 *      with the server's returned entry on success.
 *   3. On failure (Harvest 4xx → 502, Harvest 5xx → 503, network) the
 *      cache rolls back and a toast surfaces the underlying error.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { timeEntriesApi } from '@beacon/api-client';
import type { ReportDetail, TimeEntry } from '@beacon/core';

import { beaconClient } from '@/lib/api/client.js';
import { reportKeys } from './use-reports.js';

interface UpdateNotesContext {
  reportId: string;
  previousDetail: ReportDetail | undefined;
}

export function useUpdateTimeEntryNotes(reportId: string) {
  const qc = useQueryClient();

  return useMutation<
    TimeEntry,
    Error,
    { entryId: string; notes: string },
    UpdateNotesContext
  >({
    mutationFn: ({ entryId, notes }) =>
      timeEntriesApi.updateNotes(beaconClient, entryId, notes),

    onMutate: async ({ entryId, notes }) => {
      const key = reportKeys.detail(reportId);
      await qc.cancelQueries({ queryKey: key });

      const previousDetail = qc.getQueryData<ReportDetail>(key);
      if (previousDetail) {
        qc.setQueryData<ReportDetail>(key, {
          ...previousDetail,
          timeEntries: previousDetail.timeEntries.map((e) =>
            e.id === entryId ? { ...e, notes } : e
          ),
        });
      }
      return { reportId, previousDetail };
    },

    onSuccess: (server, { entryId }) => {
      const key = reportKeys.detail(reportId);
      qc.setQueryData<ReportDetail>(key, (current) => {
        if (!current) return current;
        return {
          ...current,
          timeEntries: current.timeEntries.map((e) =>
            e.id === entryId ? server : e
          ),
        };
      });
    },

    onError: (err, _vars, ctx) => {
      if (ctx?.previousDetail) {
        qc.setQueryData(
          reportKeys.detail(ctx.reportId),
          ctx.previousDetail
        );
      }
      toast.error("Couldn't save note", { description: err.message });
    },
  });
}
